import React from 'react';
import DimensionManager from './DimensionManager';

const SubscriptionInfo = ({ subscriptionData }) => {
  if (!subscriptionData) {
    return (
      <div className="section">
        <h2>Subscription Information</h2>
        <p>No subscription data available.</p>
      </div>
    );
  }

  // Determine billing model (default to PerUser if not specified)
  const billingModel = subscriptionData.billingModel || subscriptionData.BillingModel || 'PerUser';
  const isPerUserBilling = billingModel === 'PerUser';

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Helper function to format status badge
  const getStatusBadge = (status) => {
    const statusColors = {
      'Active': '#28a745',
      'Subscribed': '#28a745',
      'Suspended': '#ffc107',
      'Unsubscribed': '#dc3545',
      'PendingFulfillmentStart': '#17a2b8',
      'Pending': '#17a2b8'
    };

    const color = statusColors[status] || '#6c757d';

    return (
      <span style={{
        padding: '0.35rem 0.75rem',
        background: color,
        color: 'white',
        borderRadius: '16px',
        fontSize: '0.85rem',
        fontWeight: '600'
      }}>
        {status || 'Unknown'}
      </span>
    );
  };

  // Extract subscription properties (handle both uppercase and lowercase)
  const orgName = subscriptionData.orgName || subscriptionData.OrgName;
  const entraID = subscriptionData.entraID || subscriptionData.EntraID;
  const marketplaceSubscriptionId = subscriptionData.marketplaceSubscriptionId || subscriptionData.MarketplaceSubscriptionId;
  const subscriptionName = subscriptionData.subscriptionName || subscriptionData.SubscriptionName;
  const offerId = subscriptionData.offerId || subscriptionData.OfferId;
  const planId = subscriptionData.planId || subscriptionData.PlanId;
  const planName = subscriptionData.planName || subscriptionData.PlanName;
  const quantity = subscriptionData.quantity || subscriptionData.Quantity;
  const saasSubscriptionStatus = subscriptionData.saasSubscriptionStatus || subscriptionData.SaasSubscriptionStatus;
  const isFreeTrial = subscriptionData.isFreeTrial || subscriptionData.IsFreeTrial;
  const termUnit = subscriptionData.termUnit || subscriptionData.TermUnit;
  const startDate = subscriptionData.startDate || subscriptionData.StartDate;
  const endDate = subscriptionData.endDate || subscriptionData.EndDate;
  const beneficiaryEmail = subscriptionData.beneficiaryEmail || subscriptionData.BeneficiaryEmail;

  return (
    <div className="section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 style={{ margin: 0 }}>Subscription Information</h2>
        {isPerUserBilling ? (
          <a
            href="https://admin.cloud.microsoft/#/subscriptions"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '0.75rem 1.5rem',
              background: '#3A6A92',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              fontWeight: '600',
              fontSize: '0.95rem',
              transition: 'all 0.2s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#2f5770';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#3A6A92';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
          >
            <span>Manage in Microsoft 365</span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 8.5V12.5C12 13.0523 11.5523 13.5 11 13.5H3C2.44772 13.5 2 13.0523 2 12.5V4.5C2 3.94772 2.44772 3.5 3 3.5H7M10 2.5H14M14 2.5V6.5M14 2.5L7 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        ) : (
          <a
            href="https://portal.azure.com/#browse/Microsoft.SaaS%2Fresources"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '0.75rem 1.5rem',
              background: '#3A6A92',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              fontWeight: '600',
              fontSize: '0.95rem',
              transition: 'all 0.2s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#2f5770';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#3A6A92';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
          >
            <span>Manage in Azure Portal</span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 8.5V12.5C12 13.0523 11.5523 13.5 11 13.5H3C2.44772 13.5 2 13.0523 2 12.5V4.5C2 3.94772 2.44772 3.5 3 3.5H7M10 2.5H14M14 2.5V6.5M14 2.5L7 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        )}
      </div>

      {/* Subscription Status Card */}
      <div style={{
        /* Match main background gradient but lighter */
        background: 'linear-gradient(90deg, #1B394B 0%, #5B91AC 100%)',
        color: 'white',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '600', color: 'white' }}>
              {orgName || 'Organization Name'}
            </h3>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.1rem', opacity: 0.9 }}>
              {subscriptionName || 'Subscription Name'}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            {getStatusBadge(saasSubscriptionStatus)}
            {billingModel && (
              <span style={{
                padding: '0.35rem 0.75rem',
                background: 'rgba(255, 255, 255, 0.25)',
                color: 'white',
                borderRadius: '16px',
                fontSize: '0.85rem',
                fontWeight: '600',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}>
                {isPerUserBilling ? 'Per User Plan' : 'Flat Rate Plan'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Subscription Details Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Plan Information Card */}
        <div style={{
          background: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#495057', fontSize: '1.1rem', fontWeight: '600' }}>
            Plan Information
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div>
              <label style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: '600' }}>Offer ID</label>
              <p style={{ margin: '0.25rem 0 0 0', color: '#495057', fontFamily: 'monospace' }}>
                {offerId || 'N/A'}
              </p>
            </div>
            <div>
              <label style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: '600' }}>Plan Name</label>
              <p style={{ margin: '0.25rem 0 0 0', color: '#495057' }}>
                {planName || planId || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Azure Information Card */}
        <div style={{
          background: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#495057', fontSize: '1.1rem', fontWeight: '600' }}>
            Azure Information
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div>
              <label style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: '600' }}>Entra ID</label>
              <p style={{ margin: '0.25rem 0 0 0', color: '#495057', fontFamily: 'monospace', fontSize: '0.9rem', wordBreak: 'break-all' }}>
                {entraID || 'N/A'}
              </p>
            </div>
            <div>
              <label style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: '600' }}>Marketplace Subscription ID</label>
              <p style={{ margin: '0.25rem 0 0 0', color: '#495057', fontFamily: 'monospace', fontSize: '0.9rem', wordBreak: 'break-all' }}>
                {marketplaceSubscriptionId || 'N/A'}
              </p>
            </div>
            <div>
              <label style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: '600' }}>Beneficiary Email</label>
              <p style={{ margin: '0.25rem 0 0 0', color: '#495057' }}>
                {beneficiaryEmail || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Term Information Card */}
        <div style={{
          background: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#495057', fontSize: '1.1rem', fontWeight: '600' }}>
            Term Information
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div>
              <label style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: '600' }}>Term Unit</label>
              <p style={{ margin: '0.25rem 0 0 0', color: '#495057' }}>
                {termUnit || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Dimension Manager Section - Only show if we have subscription ID and plan ID */}
      {(subscriptionData.subscriptionID || subscriptionData.SubscriptionID) && planId && (
        <DimensionManager
          subscriptionId={subscriptionData.subscriptionID || subscriptionData.SubscriptionID}
          planId={planId}
        />
      )}

      {/* Additional Information */}
      <div style={{
        background: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '8px',
        padding: '1rem',
        marginTop: '1rem'
      }}>
        <p style={{ margin: 0, fontSize: '0.9rem', color: '#856404' }}>
          <strong>Note:</strong> This information is provided by the Azure Marketplace subscription. 
          Some fields may be unavailable depending on your subscription configuration.
        </p>
      </div>
    </div>
  );
};

export default SubscriptionInfo;
